"use client";
import Image from "next/image";
import { getStrapiMedia } from "../lib/api-helpers";
import { useEffect, useRef } from "react";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const CtaData = {
  buttonHeading: "Want to see Qualitia Boson in action?",
  buttonText: { label: "Say when.", href: "/contact-us" },
};

interface Testimonial {
  id: number;
  text: string;
  authorName: string;
  designation: string;
  fill: string;
  picture: {
    data: {
      id: string;
      attributes: {
        name: string;
        alternativeText: string;
        url: string;
      };
    };
  };
  linkedinUrl: string;
}

interface TestimonialsProps {
  data: {
    id: string;
    titleTestimonialsGroup: string;
    description: string;
    testimonials: Testimonial[];
  };
}

function Testimonial({
  id,
  text,
  authorName,
  picture,
  fill,
  designation,
  linkedinUrl,
}: Readonly<Testimonial>) {
  const imageUrl = getStrapiMedia(picture.data?.attributes.url);

  return (
    <div className="">
      <div className="relative flex h-full bg-gradient-to-b from-slate-100 to-slate-200 rounded-2xl p-[1px]">
        <div className="p-[32px] bg-white rounded-2xl" style={{ 'boxShadow': '0px 4px 2px 0px #FFFFFF80 inset' }}>
          <div className="w-[180px] h-[180px] md:w-44 md:h-44 mx-auto relative mb-[32px]">
            <Image
              className="w-[180px] h-[180px] md:w-44 md:h-44 object-cover object-top bg-[#DBDBDB] border-2 border-gray-300 rounded-full overflow-hidden"
              src={imageUrl ?? ""}
              width={100}
              height={100}
              alt={authorName}
            />

            <a target="_blank" href={linkedinUrl}>
              <Image
                src="/images/linked_in.png"
                alt={`LinkedIn image ${authorName}`}
                width={32}
                height={32}
                className="absolute bottom-0 top-auto mt-auto mb-0 rounded-full w-[32px] h-[32px] md:w-8 md:h-8 right-8 md:right-2"
                sizes="(max-width: 768px) 32px, 32px"
                priority={false}
                loading="lazy"
              />
            </a>
          </div>
          <div className="flex flex-col justify-between mt-[32px] md:mt-6 lg:mt-0 2xl:mt-0">
            <div className="flex flex-col items-center justify-center text-center">
              <p className="font-semibold !leading-[1.45] text-3xl md:text-2xl mb-[8px] text-[#001630]">{authorName}</p>
              <p className="!leading-[1.4] text-xl md:text-base text-[#001630] text-center  md:min-h-[60px] opacity-70" dangerouslySetInnerHTML={{ __html: designation }} />
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <svg width="30" height="24" viewBox="0 0 30 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="my-[32px]">
                <path opacity="0.5" d="M11.1558 0L12.8141 2.08695C8.59297 4.25418 6.33166 6.90301 6.33166 9.55184C6.33166 10.1137 6.55779 10.7559 6.8593 11.0769C7.1608 11.398 7.1608 11.398 9.04523 11.9599C11.608 12.6823 13.1156 14.8495 13.1156 17.6589C13.1156 21.2709 10.402 24 6.78392 24C2.71357 24 0 20.6288 0 15.7324C0 8.9097 4.29648 2.88963 11.1558 0ZM28.0402 0L29.6985 2.08695C25.5528 4.17391 23.2161 6.82274 23.2161 9.55184C23.2161 10.1137 23.4422 10.7559 23.7437 11.0769C24.0452 11.398 24.0452 11.398 25.9297 11.9599C28.4925 12.6823 30 14.8495 30 17.6589C30 21.2709 27.2864 24 23.6683 24C19.6734 24 16.8844 20.6288 16.8844 15.7324C16.8844 8.9097 21.1809 2.88963 28.0402 0Z" fill="#001630" />
              </svg>

              <p className="!leading-[1.4] text-xl md:text-base  text-[#001630] min-h-auto xl:min-h-28" dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials({ data }: TestimonialsProps) {
  const ctaWrapperRef = useRef(null);

  useEffect(() => {
    const ctaWrapper = ctaWrapperRef.current;

    {
      ctaWrapper && gsap.fromTo(
        ctaWrapper,
        { y: 150, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: ctaWrapper,
            toggleActions: "restart none none reverse",
            start: "top 100%", // adjust this as needed
            end: "top 30%",
          },
        }
      );
    }
  }, []);
  return (
    <div className="relative flex flex-col items-center justify-center overflow-x-hidden pt-[60px] lg:pt-[120px] 4xl:overflow-hidden z-index"
      style={{
        background: 'linear-gradient(0deg, rgba(219,242,255,1) 0%, rgba(255,255,255,1) 50%)'
      }}>
      <div className="container absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-[#75ccff79] px-8 w-full h-1/2 !hidden md:!block md:rounded-[10px] lg:rounded-[35px]"></div>
      <h2 className="text-[#032D60] text-center font-bold text-5xl 2xl:text-6xl mx-auto px-[24px] mb-[32px] lg:mb-[72px]">
        {data.titleTestimonialsGroup}
      </h2>
      <div className="container relative z-10 flex flex-col items-center px-[24px] pb-[24px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[24px] lg:gap-3 xl:gap-[24px]">
          {data.testimonials.map((testimonial: Testimonial, index: number) => (
            <Testimonial key={index} {...testimonial} />
          ))}
        </div>
      </div>
    </div>
  );
}